import type { List } from 'immutable';
import { pipe, Option, Array as A } from 'effect';
import type { IntlShape } from 'react-intl';

import { UserEnhancedApprovalFactory, type AdvancedApprovalGroupAdapter } from '@/domain/approvals/helpers';
import type { ApproversList } from '@/domain/approvals/types';
import type { CompanyUser } from '@/domain/organizationUser/schemas';
import { ColumnTypes } from '@/pages/configurations/company/pages/approvals/components/Table/helpers';

export const Fields = {
  name: 'name',
  levels: 'levels',
  approvers: 'approvers',
  minApprovers: 'minApprovers',
  minAmount: 'minAmount',
  flowType: 'flowType',
  owner: 'owner',
};

type AdvancedApprovalGroup = ReturnType<ReturnType<typeof AdvancedApprovalGroupAdapter>['toJS']>;

export type AdvancedGroupControls = Pick<AdvancedApprovalGroup, 'id' | 'name'>;

type HeadersAdapterArgs = {
  formatMessage: IntlShape['formatMessage'];
  handleEditGroup: (group: AdvancedGroupControls) => void;
  handleDeleteGroup: (group: AdvancedGroupControls) => void;
};

export const HeadersAdapter = ({ formatMessage, handleEditGroup, handleDeleteGroup }: HeadersAdapterArgs) => {
  const headers = [
    {
      field: Fields.name,
      headerName: formatMessage({
        id: 'configurations.company.approvals.groups.table.column.name',
        defaultMessage: 'Group Name',
      }),
      type: ColumnTypes.text,
      minWidth: 200,
      maxWidth: 450,
    },
    {
      field: Fields.levels,
      headerName: formatMessage({
        id: 'configurations.company.approvals.groups.table.column.approvers',
        defaultMessage: 'Approvers',
      }),
      cellRenderer: ColumnTypes.levels,
    },
    {
      field: 'date',
      headerName: formatMessage({
        id: 'configurations.company.approvals.groups.table.column.date',
        defaultMessage: 'Date',
      }),
      type: ColumnTypes.date,
    },
    {
      field: Fields.owner,
      headerName: formatMessage({
        id: 'configurations.company.approval_advancedGroups.table.column.owner',
        defaultMessage: 'Owner',
      }),
      cellRenderer: ColumnTypes.user,
      minWidth: 200,
      maxWidth: 200,
    },
    {
      field: 'controls',
      cellRenderer: ColumnTypes.controls,
      width: 120,
      maxWidth: 120,
      cellRendererParams: {
        handleEditClick: handleEditGroup,
        handleDeleteClick: handleDeleteGroup,
      },
    },
  ];

  return headers;
};

const ApproverFactory = <T extends Pick<AdvancedApprovalGroup, 'name'> & { levels: unknown; owner: unknown }>({
  name,
  levels,
  owner,
  ...rest
}: T) => ({
  [Fields.name]: name,
  [Fields.levels]: levels,
  [Fields.owner]: owner,
  ...rest,
});

const RowFactory = <T extends Pick<AdvancedApprovalGroup, 'name' | 'created_at'> & { levels: unknown; owner: unknown }>(
  value: T,
) => ({
  ...ApproverFactory(value),
  [ColumnTypes.controls]: ApproverFactory(value),
  [ColumnTypes.date]: value.created_at,
});

const RowAdapter = (
  group: AdvancedApprovalGroup,
  companyApprovers: ApproversList,
  companyUsers: ReadonlyArray<CompanyUser>,
) =>
  RowFactory({
    ...group,
    levels: group.levels.map(({ approvers, ...rest }) => ({
      ...rest,
      approvers: approvers.reduce((a, v) => {
        const user = companyApprovers.find((u) => u.id === v);
        if (user)
          return [
            ...a,
            UserEnhancedApprovalFactory({
              userGuid: user.username,
              tag: user.username,
              id: user.id,
              picture: user.picture,
              fullName: user.fullName,
            }).toJS(),
          ];
        return a;
      }, []),
    })),
    owner: pipe(
      A.findFirst(companyUsers, (user) => user.id === group.owner),
      Option.map((user) =>
        UserEnhancedApprovalFactory({
          userGuid: user.email,
          tag: user.email,
          id: user.id,
          picture: user.picture,
          fullName: `${user.first_name} ${user.last_name}`,
        }).toJS(),
      ),
      Option.getOrElse(() => UserEnhancedApprovalFactory().toJS()),
    ),
    id: group.id,
  });

export const RowsAdapter = (
  groups: List<ReturnType<typeof AdvancedApprovalGroupAdapter>>,
  companyApprovers: ApproversList,
  companyUsers: ReadonlyArray<CompanyUser>,
) => groups.toJS().map((g) => RowAdapter(g, companyApprovers, companyUsers));
